import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useReducer,
	useRef,
	useState,
} from "react";
import { closedHelp, type HelpState, helpReducer, shownTopic } from "./help-state";
import type { HelpTopicId } from "./topics";

export interface Help {
	readonly state: HelpState;
	/** The explanation shown, `null` for the overview, `undefined` when closed. */
	readonly shown: HelpTopicId | null | undefined;
	/**
	 * Where the panel is a layer over a sheet or dialog (spec admin-help: a (?) in a `Sheet` opens the
	 * help over it, also on a wide screen): that sheet's element, which the layer goes into. `null`:
	 * beside the page, or a sheet of its own.
	 */
	readonly layerHost: HTMLElement | null;
	/**
	 * A (?) or "Help" in the menu: opens `topic` (or closes it when it is what is shown). `opener` gets
	 * the focus back on closing; `layerHost` is the sheet it sits in, if any.
	 */
	readonly toggle: (
		topic: HelpTopicId | null,
		opener: HTMLElement | null,
		layerHost?: HTMLElement | null,
	) => void;
	readonly follow: (topic: HelpTopicId) => void;
	readonly back: () => void;
	/** Closes, and puts the focus back on what opened it. */
	readonly close: () => void;
	/** What opened it, for the sheet's focus on closing. */
	readonly opener: { readonly current: HTMLElement | null };
}

const HelpContext = createContext<Help | null>(null);

/** The help of the admin's frame; `null` outside it (the sign-in pages), where a (?) shows nothing. */
export const useHelp = (): Help | null => useContext(HelpContext);

/** The sheet or dialog an element sits in, but not the help panel itself. */
export function layerHostOf(element: HTMLElement): HTMLElement | null {
	const host = element.closest<HTMLElement>('[role="dialog"], [role="alertdialog"]');
	return host && !host.hasAttribute("data-help-panel") ? host : null;
}

/**
 * Holds the help panel's state for the admin's frame (`AppShell`): what is shown, the way back, what
 * opened it and where it is a layer. It stays when the employee goes to another screen.
 */
export function HelpProvider({ children }: { children: ReactNode }) {
	const [state, dispatch] = useReducer(helpReducer, closedHelp);
	const [layerHost, setLayerHost] = useState<HTMLElement | null>(null);
	const opener = useRef<HTMLElement | null>(null);

	const toggle = useCallback<Help["toggle"]>((topic, from, host = null) => {
		opener.current = from;
		setLayerHost(host);
		dispatch({ type: "toggle", topic });
	}, []);
	const close = useCallback(() => {
		dispatch({ type: "close" });
		setLayerHost(null);
		const from = opener.current;
		if (from?.isConnected) from.focus();
	}, []);
	const follow = useCallback((topic: HelpTopicId) => dispatch({ type: "follow", topic }), []);
	const back = useCallback(() => dispatch({ type: "back" }), []);

	// The sheet the layer sat in has closed: the help stays open, beside the page or as its own sheet.
	useEffect(() => {
		if (!layerHost) return;
		const observer = new MutationObserver(() => {
			if (!layerHost.isConnected) setLayerHost(null);
		});
		observer.observe(document.body, { childList: true, subtree: true });
		return () => observer.disconnect();
	}, [layerHost]);

	const help = useMemo<Help>(
		() => ({
			state,
			shown: shownTopic(state),
			layerHost: state.open ? layerHost : null,
			toggle,
			follow,
			back,
			close,
			opener,
		}),
		[state, layerHost, toggle, follow, back, close],
	);
	return <HelpContext value={help}>{children}</HelpContext>;
}
